import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { MCard, MSpinner, MCheckboxGroup } from "components/Shared";
import { getEnabledFeatures, getCaseInfo } from "actions/customer";
import { addCustomerAction, markReviewed } from "actions/underwriterActions";
import { updateCaseHeader } from "actions/homeowner/caseforms";
import { Button, Grid, Cell } from "react-md";
import {

  getCoverageAFields,
  getHazardSummaryFields,
  getCustomerCommentsFields,
  getCustomerActionsHistoryFields,
} from "typeConstants/mCardDefinitions/customer";
import { dateTimeToString } from "components/Shared/Formatters";
import { UNDERWRITING_MARK_REVIEWED_REQUIRED } from "typeConstants/customerFeatures";
import { DialogContext } from "components/Shared/DialogProvider.tsx";

class ReviewCase extends Component {
  static contextType = DialogContext;

  constructor(props) {
    super(props);
    this.getControlNumber = this.getControlNumber.bind(this);
    this.getActionRequest = this.getActionRequest.bind(this);
    this.resetFields = this.resetFields.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleMarkReviewed = this.handleMarkReviewed.bind(this);
    this.customerReviewActionsRef = React.createRef();
    this.customerCommentsRef = React.createRef();
    this.coverageAEndorsedRef = React.createRef();
    this.state = {
      selectedCustomerReviewActions: null,
      coverageAEndorsed: null,
      customerComments: null,
    };
  }

  componentDidMount() {
    const controlNumber = this.getControlNumber();
    if (controlNumber) {
      this.props.getCaseInfo(controlNumber);
    }
  }

  componentDidUpdate() {
    const { caseInfo } = this.props;
    if (caseInfo) {
      const { coverageAEndorsed } = caseInfo;
      if (!this.state.coverageAEndorsed && coverageAEndorsed) {
        this.setState({ coverageAEndorsed });
      }
      if (caseInfo.customerNumber && !this.props.fetchingEnabledFeatures && !this.props.enabledFeatures) {
        this.props.getEnabledFeatures(this.props.caseInfo.customerNumber, () => {});
      }

      this.selectedUnderwriterActions = this.props.caseInfo?.selectedUnderwriterActions;
    }
  }

  getControlNumber() {
    const { match, controlNumber } = this.props;
    return (match && match.params && match.params.controlNumber) || controlNumber;
  }

  getActionRequest() {
    return {
      controlNumber: this.getControlNumber(),
      caseActions: this.getSelectedActions(),
      comments: this.customerCommentsRef.current.value,
    };
  }

  getSelectedActions() {
    const { caseInfo: { availableUnderwriterActions } } = this.props;
    const selectedCodes = this.customerReviewActionsRef.current.getChecked();
    return availableUnderwriterActions.filter((item) => { return selectedCodes.includes(item.code); });
  }

  resetFields() {
    this.customerCommentsRef.current.value = "";
  }

  handleSave() {
    const selectedActions = this.getSelectedActions();
    // If this feature is enabled, the case must be marked reviewed to take an underwriter action
    if (selectedActions.length > 0
        && this.props.enabledFeatures?.map(s => s.feature).includes(UNDERWRITING_MARK_REVIEWED_REQUIRED)
        && !this.props.caseInfo?.dateReviewed) {
      this.context.showYesNo(
        null,
        "Case must be marked reviewed when entering initial reviewer action. Should the case be marked as reviewed?",
        this.handleMarkReviewed,
        () => {},
        "Confirm",
        "Cancel");
    } else {
      this.props.addCustomerAction(this.getActionRequest());
      this.resetFields();
      this.props.updateCaseHeader(this.getControlNumber(), { coverageAEndorsed: this.state.coverageAEndorsed });
      this.selectedUnderwriterActions = this.props.caseInfo.availableUnderwriterActions.filter((action) => selectedActions.some(s => s.code === action.code));
    }
  }

  handleMarkReviewed() {
    this.props.markReviewed(this.getActionRequest());
    this.resetFields();
    this.props.updateCaseHeader(this.getControlNumber(), { coverageAEndorsed: this.state.coverageAEndorsed });
    const selectedActions = this.getSelectedActions();
    this.selectedUnderwriterActions = this.props.caseInfo.availableUnderwriterActions.filter((action) => selectedActions.some(s => s.code === action.code));
  }

  render() {
    if (!this.props.caseInfo || this.props.fetchingEnabledFeatures)
      return <MSpinner />;
    const {
      caseInfo: {
        availableUnderwriterActions,
        hazards,
        coverageAIn,
        coverageAOut,
        coverageAEndorsed,
        underwriterActionsAudits,
        dateReviewed,
        reviewedBy,
      },
    } = this.props;
    const uwActionCheckboxes = availableUnderwriterActions
        .filter(a => a.isUserSelectable)
        .map(({name, code}) => ({
          value: code,
          label: name,
          defaultChecked: (this.selectedUnderwriterActions ?? this.props.caseInfo?.selectedUnderwriterActions)?.some((action) => action.code === code) ?? false,
        }));
    return (
      <>
        <Grid>
          <Cell size={12}>
            <MCard
              className="underwriter-actions-card"
              title={"Reviewer Action Taken"}
              fields={[{
                key: "reviewer-actions-taken",
                control: (
                  <MCheckboxGroup
                    controlMethods={cm => this.customerReviewActionsRef.current = cm}
                    fields={uwActionCheckboxes}
                    cellSize={4}
                    containerClassName={"reviewer-actions-checkbox-container"}
                  />
                )
              }]}
              disableHoverStyles
            />
          </Cell>
          <Cell size={12}>
            <MCard
              title={"Replacement Cost & Hazards"}
              fields={[
                getCoverageAFields({ coverageAIn, coverageAOut, coverageAEndorsed }, true),
                getHazardSummaryFields(hazards, true),
              ]}
              onEditFinished={({ value }) => this.setState({ coverageAEndorsed: value })}
              onlyEditable
            />
          </Cell>
          <Cell size={12}>
            <MCard
              title={"Review Comments"}
              fields={getCustomerCommentsFields({ innerRef: this.customerCommentsRef })}
            />
          </Cell>
          <Cell size={12}>
            <MCard
              title={"Actions History"}
              fields={getCustomerActionsHistoryFields(underwriterActionsAudits)}
              disableHoverStyles
            />
          </Cell>
          <Cell size={12}>
            <Button
              onClick={this.handleSave}
              raised
              primary
              children={"Save"}
            />
            {!dateReviewed &&
              <Button
                className="underwriter-mark-reviewed-button"
                onClick={this.handleMarkReviewed}
                raised
                primary
                children={"Mark Reviewed"}
              />}
            {dateReviewed && <span className="underwriter-reviewed-by">{`Reviewed by ${reviewedBy} on ${dateTimeToString(dateReviewed)}`} </span>}
          </Cell>
        </Grid>
      </>
    );
  }
}

ReviewCase.propTypes = {
  // Pure Props
  controlNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  // Router Props
  match: PropTypes.shape({
    params: PropTypes.shape({
      controlNumber: PropTypes.string.isRequired
    })
  }),

  // Redux State Props
  caseInfo: PropTypes.object,
  enabledFeatures: PropTypes.array,
  fetchingEnabledFeatures: PropTypes.bool,

  //Redux Action Props
  getCaseInfo: PropTypes.func.isRequired,
  addCustomerAction: PropTypes.func.isRequired,
  updateCaseHeader: PropTypes.func.isRequired,
  updateHeader: PropTypes.func.isRequired,
  useDispatch: PropTypes.func.isRequired,
  markReviewed: PropTypes.func.isRequired,
  getEnabledFeatures: PropTypes.func,
};

const mapStateToProps = state => ({
  caseInfo: state.homeownerCustomer.caseInfo,
  enabledFeatures: state.customer.enabledFeatures,
  fetchingEnabledFeatures: state.customer.fetchingEnabledFeatures,
});

const mapDispatchToProps = dispatch => ({
  getCaseInfo(controlNumber) {
    dispatch(getCaseInfo(controlNumber));
  },
  addCustomerAction(action) {
    dispatch(addCustomerAction(action));
  },
  updateCaseHeader(controlNumber, fields) {
    dispatch(updateCaseHeader(controlNumber, fields));
  },
  markReviewed(action) {
    dispatch(markReviewed(action));
  },
  getEnabledFeatures(customerNumber, callback) {
    dispatch(getEnabledFeatures(customerNumber, callback));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewCase);
